import classNames from 'classnames';
import {useBoolean} from 'hooks/useBoolean';
import {ComponentProps, useCallback, useEffect, useRef, useState} from 'react';

import {Icon} from 'components/Icon/Icon';
import {TextField} from 'components/TextField/TextField';

import styles from './Select.module.css';

type Value = string | number;

type MenuItemExtend = {id: Value; label: string};

type Props<MenuItem> = {
    className?: string;
    renderItemMenu: (item: MenuItem) => JSX.Element;
    options: MenuItem[];
    value?: Value;
    onSelect: (item: MenuItem) => void;
    onChange?: (value: string) => void;
} & Pick<ComponentProps<typeof TextField>, 'name' | 'label'>;

export function Select<MenuItem extends MenuItemExtend>({
    name,
    label,
    value,
    className,
    options,
    onSelect,
    renderItemMenu,
    onChange,
}: Props<MenuItem>) {
    const {value: hasFocus, toggle: toggleHasFocus} = useBoolean();
    const [inputValue, setInputValue] = useState('');

    const isTyping = useRef(false);

    //если value пришло новое, то обновляем текст в инпуте
    useEffect(() => {
        // если в данный момент пользователь вводить текст, то менять програмно его нельзя
        if (isTyping.current === true) {
            return;
        }

        const currentSelectedOptions = options.find(
            (item) => item.id === value,
        );
        setInputValue(currentSelectedOptions?.label || '');
    }, [value, options]);

    const handleChange = useCallback(
        (value: string) => {
            isTyping.current = true;
            setInputValue(value);
            onChange?.(value);
        },
        [setInputValue, onChange],
    );

    const handleFocus = useCallback(() => {
        toggleHasFocus(true);
    }, [toggleHasFocus]);

    const handleBlur = useCallback(() => {
        toggleHasFocus(false);
    }, [toggleHasFocus]);

    const handleToggleMenu = useCallback(() => {
        toggleHasFocus();
    }, [toggleHasFocus]);

    const makeHandleClickItem = useCallback(
        (item: MenuItem) => () => {
            isTyping.current = false;
            onSelect(item);
        },
        [onSelect],
    );

    const editable = Boolean(onChange);

    const shouldRenderMenu = hasFocus && Boolean(options.length);

    return (
        <div
            className={classNames(styles.block, className, {
                [styles.collapsed]: shouldRenderMenu,
                [styles.select]: !editable,
            })}
            onClick={editable ? undefined : handleToggleMenu}
        >
            <TextField
                name={name}
                value={inputValue}
                label={label}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                readonly={!editable}
                hideClearButton={!editable}
                disabled={!editable}
                inputClassName={classNames({
                    [styles.disabledInput]: !editable,
                })}
            />
            {!editable && (
                <Icon name="arrow" className={classNames(styles.arrowIcon)} />
            )}

            {shouldRenderMenu && (
                <div className={styles.menu}>
                    {options.map((item) => (
                        <div
                            key={item.id}
                            onMouseDown={makeHandleClickItem(item)}
                        >
                            {renderItemMenu(item)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
